import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { Option } from "../../models/Option";
import { InputValidation } from "../../models/InputValidation";
import { ResponseModel } from "../../models/ResponseModel";
import { VideoLesson } from "../../models/VideoLesson";
import { VideoSubject } from "../../models/VideoSubject";
import { approveVideoLesson, getTeacherVideoSubjects, getVideoLesson, getVideoLessonTeachers, saveVideoLesson, uploadLessonTute, uploadLessonVideo } from "../../services/VideoLessonService";
import { getFormatedDate, getVideoLessonTuteUrl, validatePDF, validateVideo } from "../../helpers/Common";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { useAlert } from "../../hooks/useAlert";
import { Header } from "../../components/shared/Header";
import { Button } from "../../components/shared/Button";
import { Input } from "../../components/shared/inputs/Input";
import { Switch } from "../../components/shared/inputs/Switch";
import { Select } from "../../components/shared/Select";
import { QuillEditor } from "../../components/shared/QuillEditor";
import { PopUpDialog } from "../../components/shared/dialogs/PopUpDialog";
import { SearchContentLoader } from "../../components/loaders/SearchContentLoader";
import { SelectLoader } from "../../components/loaders/SelectLoader";

const MAX_IMAGE_SIZE = 2097152;

const newLesson: VideoLesson = {
    videoLessonId: 0,
    videoSubjectId: 0,
    lessonName: "",
    hasTute: false,
    paymentRequired: true
};

export const VideoLessonEditPage: React.FC = () => {
    const params = useParams();
    const lessonId: number = +params.lessonId!;
    const isNew = lessonId === 0;
    const navigate = useNavigate();
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("videoLessonEditPage"));
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();
    const [lesson, setLesson] = useState<VideoLesson>();
    const [subject, setSubject] = useState<VideoSubject>();
    // Teacher and subject pickers are only used when creating a lesson
    const [teachers, setTeachers] = useState<Option[]>();
    const [teacherId, setTeacherId] = useState<number>();
    const [teacherSubjects, setTeacherSubjects] = useState<Option[]>();
    const [nameValidation, setNameValidation] = useState<InputValidation>({});
    const [priceValidation, setPriceValidation] = useState<InputValidation>({});
    const [subjectValidation, setSubjectValidation] = useState<InputValidation>({});
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [imageFile, setImageFile] = useState<File>();
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string>();
    const [imageValidation, setImageValidation] = useState<InputValidation>({});
    const [videoFile, setVideoFile] = useState<File>();
    const [videoPreviewUrl, setVideoPreviewUrl] = useState<string>();
    const [tuteFile, setTuteFile] = useState<File>();
    const [savingStep, setSavingStep] = useState<string>();
    const [successMessage, setSuccessMessage] = useState<string>();
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();
    const { Alert, openAlert } = useAlert();

    useEffect(() => {
        if (admin === null) {
            // Callback url defaults to the current path, so the admin returns to this lesson after login
            toAdminLogin("Please login to manage video lessons.");
            return;
        }
        let active = true;
        window.scrollTo(0, 0);
        (async () => {
            try {
                if (isNew) {
                    const teacherList: Option[] = await getVideoLessonTeachers(admin);
                    if (!active) return;
                    setTeachers(teacherList);
                    setLesson(newLesson);
                }
                else {
                    const data: { subject: VideoSubject, lesson: VideoLesson } = await getVideoLesson(admin, lessonId);
                    if (!active) return;
                    setSubject(data.subject);
                    setLesson(data.lesson);
                    setImagePreviewUrl(data.lesson.lessonImageUrl);
                    setVideoPreviewUrl(data.lesson.lessonVideoUrl);
                }
            } catch (error) {
                if (active) toErrorPage(error);
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        setStyles(styleContext.getComponentStyle("videoLessonEditPage"));
    }, [isMobile]);

    const selectTeacher = async (selectedTeacherId?: number) => {
        setTeacherId(selectedTeacherId);
        setTeacherSubjects(undefined);
        setLesson((values) => ({ ...values!, videoSubjectId: 0 }));
        if (!selectedTeacherId) return;
        try {
            const subjectList: VideoSubject[] = await getTeacherVideoSubjects(admin!, selectedTeacherId);
            setTeacherSubjects(subjectList.map((videoSubject) => ({
                value: videoSubject.videoSubjectId,
                primaryOption: videoSubject.subjects ?? `Subject ${videoSubject.videoSubjectId}`,
                secondaryOption: videoSubject.grades,
                checked: false
            })));
        } catch (error) {
            toErrorPage(error);
        }
    }

    const selectSubject = (selectedSubjectId?: number) => {
        setLesson((values) => ({ ...values!, videoSubjectId: selectedSubjectId ?? 0 }));
        if (selectedSubjectId) {
            setSubjectValidation({ valid: true, invalid: false });
        }
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = event.target;
        setLesson((values) => ({ ...values!, [name]: type === "number" ? (value === "" ? undefined : Number(value)) : value }));
    }

    const handleDescriptionChange = (name: string, content: string) => {
        setLesson((values) => ({ ...values!, description: content }));
    }

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (file.size > 0 && file.size <= MAX_IMAGE_SIZE) {
            setImageFile(file);
            setImagePreviewUrl(URL.createObjectURL(file));
            setImageValidation({ valid: true, invalid: false });
        }
        else {
            setImageValidation({ valid: false, invalid: true, invalidMessage: "File size is invalid" });
        }
    }

    const handleVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const validation = validateVideo(file);
        if (validation.valid) {
            setVideoFile(file);
            setVideoPreviewUrl(URL.createObjectURL(file));
        }
        else {
            openAlert("error", validation.invalidMessage!);
        }
    }

    const handleTuteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const validation = validatePDF(file);
        if (validation.valid) {
            setTuteFile(file);
        }
        else {
            openAlert("error", validation.invalidMessage!);
        }
    }

    const validateForm = (approve: boolean): boolean => {
        let isValid = true;
        if (!lesson!.lessonName || lesson!.lessonName.trim() === "") {
            setNameValidation({ valid: false, invalid: true, invalidMessage: "Lesson name is required" });
            isValid = false;
        }
        else {
            setNameValidation({ valid: true, invalid: false });
        }
        if (lesson!.paymentRequired && !(lesson!.price && lesson!.price > 0)) {
            setPriceValidation({ valid: false, invalid: true, invalidMessage: "Price is required for a paid lesson" });
            isValid = false;
        }
        else {
            setPriceValidation({ valid: true, invalid: false });
        }
        if (isNew && !lesson!.videoSubjectId) {
            setSubjectValidation({ valid: false, invalid: true, invalidMessage: "Select a teacher and subject" });
            isValid = false;
        }
        // Same rule as the API: approving publishes the lesson, so it needs a video
        if (approve && !lesson!.lessonVideoUrl && !videoFile) {
            openAlert("error", "Upload a video before approving this lesson.");
            isValid = false;
        }
        return isValid;
    }

    // Order matters: the lesson must exist before its files upload, and approval checks the recorded video
    const save = async (approve: boolean) => {
        if (!validateForm(approve)) return;
        try {
            setSavingStep("Saving lesson...");
            const formData = new FormData();
            formData.append('modelString', encodeURIComponent(JSON.stringify(lesson)));
            if (imageFile) {
                formData.append('file', imageFile);
            }
            const response: ResponseModel = await saveVideoLesson(admin!, formData);
            if (!response.status) {
                setSavingStep(undefined);
                openAlert("error", response.message || "Oops! something went wrong.");
                return;
            }
            const savedLessonId: number = response.data;
            if (videoFile) {
                setSavingStep("Uploading video...");
                await uploadLessonVideo(admin!, savedLessonId, videoFile);
            }
            if (tuteFile) {
                setSavingStep("Uploading tute...");
                await uploadLessonTute(admin!, savedLessonId, tuteFile);
            }
            if (approve) {
                setSavingStep("Approving lesson...");
                const approveResponse: ResponseModel = await approveVideoLesson(admin!, savedLessonId);
                if (!approveResponse.status) {
                    setSavingStep(undefined);
                    openAlert("error", `Lesson saved, but approval failed: ${approveResponse.message || "Oops! something went wrong."}`);
                    return;
                }
            }
            setSavingStep(undefined);
            setSuccessMessage(approve
                ? "The lesson is saved and approved. It is now visible to students."
                : isNew ? "The lesson is created and waiting for approval." : "The lesson changes are saved.");
        } catch (error) {
            setSavingStep(undefined);
            toErrorPage(error);
        }
    }

    const closeSuccessDialog = () => {
        setSuccessMessage(undefined);
        navigate('/video-lessons');
    }

    // Going back keeps the list's tab, teacher filter and page; opened directly (no in-app history), fall back to the list
    const goBack = () => {
        if (window.history.state?.idx > 0) {
            navigate(-1);
        }
        else {
            navigate('/video-lessons');
        }
    }

    if (admin === null) return null;

    const isSaving = savingStep !== undefined;

    return (
        <>
            <Header />
            <div style={styles}>
                <h1 style={styles.heading}>{isNew ? "New Video Lesson" : `Video Lesson #${lessonId}`}</h1>
                {lesson === undefined ? <SearchContentLoader /> :
                <div style={styles.columns}>
                    <div style={styles.column}>
                        {isNew ?
                        <div style={styles.section}>
                            <label style={styles.label}>{"Teacher"}</label>
                            {teachers === undefined ? <SelectLoader count={1} /> :
                            <Select styles={styles.teacherSelect} label={"Select teacher"} options={teachers} getSelectedValue={selectTeacher} />}
                            {teacherId && <>
                                <label style={styles.label}>{"Subject"}</label>
                                {teacherSubjects === undefined ? <SelectLoader count={1} /> :
                                // Keyed by teacher so the subject choice resets when the teacher changes
                                <Select key={teacherId} styles={styles.subjectSelect} label={"Select subject"} options={teacherSubjects} getSelectedValue={selectSubject} />}
                            </>}
                            {subjectValidation.invalid && <div style={styles.invalidMessage}>{subjectValidation.invalidMessage}</div>}
                        </div> :
                        <SubjectSummary subject={subject} lesson={lesson} styles={styles.summary} />}
                        <div style={styles.section}>
                            <Input type="text" name="lessonName" label="Lesson Name" value={lesson.lessonName} onChange={handleInputChange} validation={nameValidation} />
                        </div>
                        <div style={styles.switchWrapper} onClick={() => setLesson((values) => ({ ...values!, paymentRequired: !values!.paymentRequired }))}>
                            <span style={styles.switchLabel}>{"Paid lesson"}</span>
                            <Switch checked={lesson.paymentRequired} />
                        </div>
                        {lesson.paymentRequired &&
                        <div style={styles.section}>
                            <Input type="number" name="price" label="Price (LKR)" value={lesson.price} onChange={handleInputChange} validation={priceValidation} />
                        </div>}
                        <div style={styles.section}>
                            <label style={styles.label}>{"Description"}</label>
                            <QuillEditor name="description" value={lesson.description} onChange={handleDescriptionChange} styles={styles.editor} />
                        </div>
                    </div>
                    <div style={styles.column}>
                        <div style={styles.section}>
                            <label style={styles.label}>{"Lesson Image (max 2 MB)"}</label>
                            {imagePreviewUrl ?
                            <img src={imagePreviewUrl} alt="lesson" onClick={() => imageInputRef.current?.click()} style={{...styles.image, ...(imageValidation.invalid && styles.invalidBorder)}} /> :
                            <div style={{...styles.placeholder, ...(imageValidation.invalid && styles.invalidBorder)}} onClick={() => imageInputRef.current?.click()}>{"Click to upload image"}</div>}
                            <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} ref={imageInputRef} />
                            {imageValidation.invalid && <div style={styles.invalidMessage}>{imageValidation.invalidMessage}</div>}
                        </div>
                        <div style={styles.section}>
                            <label style={styles.label}>{"Video (mp4)"}</label>
                            {videoPreviewUrl ? <video style={styles.video} controls src={videoPreviewUrl} /> : <div style={styles.warningText}>{"No video uploaded yet"}</div>}
                            <label style={styles.fileButton}>
                                {videoFile ? `Selected: ${videoFile.name}` : (videoPreviewUrl ? "Replace video" : "Choose video")}
                                <input type="file" accept="video/mp4" onChange={handleVideoChange} style={{ display: 'none' }} />
                            </label>
                        </div>
                        <div style={styles.section}>
                            <label style={styles.label}>{"Tute (pdf)"}</label>
                            {lesson.hasTute && !isNew &&
                            <a style={styles.link} href={getVideoLessonTuteUrl(lessonId, admin.adminId, admin.token)} target="_blank" rel="noreferrer">
                                {`Open current tute${lesson.tuteName ? `: ${lesson.tuteName}` : ""}`}
                            </a>}
                            <label style={styles.fileButton}>
                                {tuteFile ? `Selected: ${tuteFile.name}` : (lesson.hasTute ? "Replace tute" : "Choose tute")}
                                <input type="file" accept="application/pdf" onChange={handleTuteChange} style={{ display: 'none' }} />
                            </label>
                        </div>
                    </div>
                </div>}
                {lesson !== undefined &&
                <div style={styles.buttonWrapper}>
                    <Button style={{...styles.backButton, ...(isSaving && styles.disabledButton)}} disabled={isSaving} onClick={goBack}>{"Back"}</Button>
                    {isSaving && <span style={styles.savingText}>{savingStep}</span>}
                    <Button style={{...styles.secondaryButton, ...(isSaving && styles.disabledButton)}} disabled={isSaving} onClick={() => save(false)}>{"Save"}</Button>
                    {(isNew || lesson.pending) &&
                    <Button style={{...styles.primaryButton, ...(isSaving && styles.disabledButton)}} disabled={isSaving} onClick={() => save(true)}>{"Save & Approve"}</Button>}
                </div>}
            </div>
            <PopUpDialog styles={styles.responseDialog} open={successMessage !== undefined} toggle={closeSuccessDialog}>
                <h3 style={styles.responseTitle}>{"Success"}</h3>
                <p>{successMessage}</p>
                <Button style={styles.responseButton} type="button" onClick={closeSuccessDialog}>{"OK"}</Button>
            </PopUpDialog>
            <Alert />
        </>
    )
}

interface SubjectSummaryProps {
    subject?: VideoSubject;
    lesson: VideoLesson;
    styles: any;
}

// Who the lesson belongs to and where it is in review; pending with an approved date is an edit of a live lesson
const SubjectSummary: React.FC<SubjectSummaryProps> = ({ subject, lesson, styles }) => {
    const reviewState = lesson.pending
        ? (lesson.approvedDate ? "Edited – awaiting re-approval (hidden from new students)" : "New – awaiting approval")
        : "Approved – visible to students";

    return (
        <div style={styles.container}>
            <div style={styles.subjects}>{subject?.subjects}</div>
            {subject?.grades && <div style={styles.muted}>{subject.grades}</div>}
            <div style={styles.muted}>{`Teacher: ${subject?.teacherName ?? "-"}`}</div>
            <div style={lesson.pending ? styles.pendingState : styles.approvedState}>{reviewState}</div>
            {lesson.approvedDate && <div style={styles.muted}>{`Last approved: ${getFormatedDate(lesson.approvedDate)}`}</div>}
        </div>
    );
}
